<template>
  <div class="flashcard-canvas-container flex-1 flex flex-col items-center justify-center p-2 sm:p-4 w-full">
    <!-- Active Card Presentation -->
    <div
      v-if="card"
      class="flashcard-card w-full max-w-lg rounded-2xl border p-6 sm:p-8 flex flex-col items-center text-center shadow-xl transition-all"
    >
      <!-- Image attachment if present -->
      <div v-if="card.imageUrl" class="card-image-wrapper mb-4 w-full overflow-hidden rounded-xl border border-black/5 dark:border-white/5">
        <img :src="card.imageUrl" alt="Card Illustration" class="w-full max-h-56 object-cover rounded-xl" />
      </div>

      <!-- Informational / Menu View -->
      <div
        v-if="card.isInformational || (!card.translation && !card.transcription && !card.isTypingDrill && card.word.length > 30)"
        class="info-content text-center py-4 w-full"
      >
        <div class="text-4xl mb-3 select-none">🦉</div>
        <div class="text-base font-medium whitespace-pre-wrap leading-relaxed text-[var(--ui-text)]">
          {{ card.rawText || card.word }}
        </div>
      </div>

      <!-- Translation Result View from server -->
      <div v-else-if="card.isTranslationResult" class="w-full flex flex-col items-center">
        <UiBadge
          :variant="card.isCorrect ? 'primary' : 'warning'"
          size="sm"
          class="mb-3 px-3 py-1 font-semibold tracking-wide"
        >
          {{ card.isCorrect ? '✅ Translation is Correct!' : '📛 Wrong translation' }}
        </UiBadge>

        <div class="my-3 text-center">
          <h2 class="text-3xl font-extrabold text-[var(--ui-text)]">
            {{ card.word }} <span v-if="card.translation" class="text-xl font-normal text-[var(--ui-text-muted)]">— {{ card.translation }}</span>
          </h2>
          <p v-if="card.transcription" class="text-sm font-mono text-[var(--ui-text-muted)] mt-1">
            [{{ card.transcription }}]
          </p>
        </div>
      </div>

      <!-- Level 5: Writing Drill Mode -->
      <div v-else-if="card.isTypingDrill || card.level === 5" class="w-full flex flex-col items-center">
        <!-- Level Badge -->
        <UiBadge variant="primary" size="sm" class="mb-3 px-3 py-1 font-semibold tracking-wide">
          Level 5 ⭐⭐⭐⭐⭐ — Writing Drill
        </UiBadge>

        <!-- Prompt (Translation / Meaning) -->
        <div class="my-2">
          <p class="text-xs font-bold uppercase tracking-wider text-[var(--ui-text-muted)] mb-1">Translate into target language:</p>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-[var(--ui-text)]">
            {{ card.translation || card.word }}
          </h2>
        </div>

        <p class="text-xs font-bold uppercase text-[var(--ui-primary)] my-2">
          🔽 Please, write translation 🔽
        </p>

        <!-- Typing Drill Input & Action -->
        <div class="w-full max-w-sm flex flex-col items-center gap-2 my-3">
          <div class="flex w-full gap-2">
            <input
              v-model="userTranslationInput"
              type="text"
              placeholder="Type translation here..."
              class="ui-input flex-1 text-center font-medium"
              :disabled="isTypingSubmitted"
              @keydown.enter.prevent="submitTyping"
            />
            <UiButton
              variant="primary"
              :disabled="!userTranslationInput.trim() || isTypingSubmitted"
              @click="submitTyping"
            >
              Check
            </UiButton>
          </div>

          <!-- Evaluation feedback -->
          <div
            v-if="isTypingSubmitted"
            class="w-full mt-2 p-3 rounded-xl border text-sm text-left transition-all"
            :class="isTypingCorrect ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-400'"
          >
            <div class="font-bold flex items-center gap-2">
              <i :class="isTypingCorrect ? 'fa-solid fa-circle-check text-emerald-500' : 'fa-solid fa-circle-xmark text-rose-500'"></i>
              <span>{{ isTypingCorrect ? '✅ Translation is Correct!' : '📛 Wrong translation' }}</span>
            </div>
            <p v-if="card.word" class="mt-1 text-xs opacity-90">
              Expected answer: <strong>{{ card.word }}</strong> <span v-if="card.transcription">[{{ card.transcription }}]</span>
            </p>
          </div>
        </div>

        <!-- Word Actions Toolbar below word -->
        <div class="word-actions-toolbar flex flex-wrap items-center justify-center gap-1.5 my-3 py-1 px-2 rounded-xl bg-[var(--ui-surface-subtle)] border border-[var(--ui-border)]">
          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)]"
            title="Listen to pronunciation"
            @click="playPronunciation"
          >
            <i class="fa-solid fa-volume-high mr-1 text-[var(--ui-primary)]"></i>
            <span>Listen</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-blue-500"
            title="Promote star level"
            @click="handleReaction('star_up')"
          >
            <i class="fa-solid fa-thumbs-up mr-1 text-blue-500"></i>
            <span>+1 Star</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-slate-400"
            title="Demote star level"
            @click="handleReaction('star_down')"
          >
            <i class="fa-solid fa-thumbs-down mr-1 text-slate-400"></i>
            <span>-1 Star</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-amber-500"
            title="Postpone word"
            @click="handleReaction('postpone')"
          >
            <i class="fa-solid fa-clock mr-1 text-amber-500"></i>
            <span>Postpone</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-rose-500"
            title="Mark as learned"
            @click="handleReaction('learned')"
          >
            <i class="fa-solid fa-heart mr-1 text-rose-500"></i>
            <span>Learned</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-gray-400"
            title="Remove word"
            @click="handleReaction('trash')"
          >
            <i class="fa-solid fa-trash mr-1 text-gray-400"></i>
            <span>Remove</span>
          </UiButton>
        </div>
      </div>

      <!-- Level 4: Reverse Card Mode -->
      <div v-else-if="card.isReverse || card.level === 4" class="w-full flex flex-col items-center">
        <!-- Level Badge -->
        <UiBadge variant="warning" size="sm" class="mb-3 px-3 py-1 font-semibold tracking-wide">
          Level 4 ⭐⭐⭐⭐ — Recall Drill (Reverse)
        </UiBadge>

        <!-- Prompt (Translation visible) -->
        <div class="my-2">
          <p class="text-xs font-bold uppercase tracking-wider text-[var(--ui-text-muted)] mb-1">Recall target word:</p>
          <h2 class="text-3xl sm:text-4xl font-extrabold text-[var(--ui-text)]">
            {{ card.translation }}
          </h2>
        </div>

        <!-- Target word behind spoiler -->
        <div class="my-3 w-full flex flex-col items-center justify-center">
          <div v-if="isRevealed" class="flex flex-col items-center gap-1">
            <div class="flex items-center gap-2">
              <span class="text-2xl sm:text-3xl font-extrabold word-text">{{ card.word }}</span>
              <button
                type="button"
                class="audio-button w-8 h-8 rounded-full hover:scale-105 active:scale-95 transition flex items-center justify-center shadow-xs"
                title="Pronounce word"
                @click="playPronunciation"
              >
                <i class="fa-solid fa-volume-high text-sm text-[var(--ui-primary)]"></i>
              </button>
            </div>
            <p v-if="card.transcription" class="text-sm font-mono text-[var(--ui-text-muted)]">
              [{{ card.transcription }}]
            </p>
          </div>
          <UiButton
            v-else
            variant="secondary"
            shape="pill"
            class="px-5 py-2 text-sm font-semibold transition"
            @click="isRevealed = true"
          >
            <i class="fa-solid fa-eye mr-2"></i>
            <span>Tap to reveal target word</span>
          </UiButton>
        </div>

        <!-- Word Actions Toolbar below word -->
        <div class="word-actions-toolbar flex flex-wrap items-center justify-center gap-1.5 my-3 py-1 px-2 rounded-xl bg-[var(--ui-surface-subtle)] border border-[var(--ui-border)]">
          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)]"
            title="Listen to pronunciation"
            @click="playPronunciation"
          >
            <i class="fa-solid fa-volume-high mr-1 text-[var(--ui-primary)]"></i>
            <span>Listen</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-blue-500"
            title="Promote star level"
            @click="handleReaction('star_up')"
          >
            <i class="fa-solid fa-thumbs-up mr-1 text-blue-500"></i>
            <span>+1 Star</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-slate-400"
            title="Demote star level"
            @click="handleReaction('star_down')"
          >
            <i class="fa-solid fa-thumbs-down mr-1 text-slate-400"></i>
            <span>-1 Star</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-amber-500"
            title="Postpone word"
            @click="handleReaction('postpone')"
          >
            <i class="fa-solid fa-clock mr-1 text-amber-500"></i>
            <span>Postpone</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-rose-500"
            title="Mark as learned"
            @click="handleReaction('learned')"
          >
            <i class="fa-solid fa-heart mr-1 text-rose-500"></i>
            <span>Learned</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-gray-400"
            title="Remove word"
            @click="handleReaction('trash')"
          >
            <i class="fa-solid fa-trash mr-1 text-gray-400"></i>
            <span>Remove</span>
          </UiButton>
        </div>
      </div>

      <!-- Standard Levels 1-3 View -->
      <template v-else>
        <!-- Level Badge -->
        <UiBadge
          :variant="card.level === 3 ? 'warning' : 'neutral'"
          size="sm"
          class="mb-3 px-3 py-1 font-semibold tracking-wide"
        >
          Level {{ card.level || 1 }} {{ '⭐'.repeat(card.level || 1) }}
        </UiBadge>

        <!-- Main Word & Pronounce -->
        <div class="word-header flex items-center justify-center gap-3 w-full my-2">
          <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight word-text">
            {{ card.word }}
          </h2>
          <button
            type="button"
            class="audio-button w-10 h-10 rounded-full hover:scale-105 active:scale-95 transition flex items-center justify-center shadow-sm"
            :class="{ 'opacity-50 pointer-events-none': isPlayingAudio }"
            title="Pronounce word"
            @click="playPronunciation"
          >
            <i class="fa-solid fa-volume-high text-lg text-[var(--ui-primary)]"></i>
          </button>
        </div>

        <!-- Transcription -->
        <p v-if="card.transcription" class="transcription-text text-sm sm:text-base font-mono text-[var(--ui-text-muted)] mb-2">
          [{{ card.transcription }}]
        </p>

        <!-- Word Actions Toolbar directly below word -->
        <div class="word-actions-toolbar flex flex-wrap items-center justify-center gap-1.5 my-2 py-1 px-2 rounded-xl bg-[var(--ui-surface-subtle)] border border-[var(--ui-border)]">
          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)]"
            title="Listen to pronunciation"
            @click="playPronunciation"
          >
            <i class="fa-solid fa-volume-high mr-1 text-[var(--ui-primary)]"></i>
            <span>Listen</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-blue-500"
            title="Promote star level"
            @click="handleReaction('star_up')"
          >
            <i class="fa-solid fa-thumbs-up mr-1 text-blue-500"></i>
            <span>+1 Star</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-slate-400"
            title="Demote star level"
            @click="handleReaction('star_down')"
          >
            <i class="fa-solid fa-thumbs-down mr-1 text-slate-400"></i>
            <span>-1 Star</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-amber-500"
            title="Postpone word"
            @click="handleReaction('postpone')"
          >
            <i class="fa-solid fa-clock mr-1 text-amber-500"></i>
            <span>Postpone</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-rose-500"
            title="Mark as learned"
            @click="handleReaction('learned')"
          >
            <i class="fa-solid fa-heart mr-1 text-rose-500"></i>
            <span>Learned</span>
          </UiButton>

          <UiButton
            variant="ghost"
            size="sm"
            shape="pill"
            class="text-xs px-2.5 py-1 text-[var(--ui-text)] hover:text-gray-400"
            title="Remove word"
            @click="handleReaction('trash')"
          >
            <i class="fa-solid fa-trash mr-1 text-gray-400"></i>
            <span>Remove</span>
          </UiButton>
        </div>

        <!-- Divider -->
        <div class="w-16 h-0.5 bg-[var(--ui-border)] my-2"></div>

        <!-- Translation (with tap-to-reveal spoiler support for Level 3) -->
        <div v-if="card.translation" class="translation-section my-2 w-full flex justify-center">
          <p
            v-if="!card.isSpoiler || isRevealed"
            class="text-xl sm:text-2xl font-semibold text-[var(--ui-text)]"
          >
            {{ card.translation }}
          </p>
          <UiButton
            v-else
            variant="secondary"
            shape="pill"
            class="px-5 py-2 text-sm font-semibold transition"
            @click="isRevealed = true"
          >
            <i class="fa-solid fa-eye mr-2"></i>
            <span>Tap to reveal translation</span>
          </UiButton>
        </div>

        <!-- Examples / Hints -->
        <div v-if="card.examples && card.examples.length > 0" class="examples-section w-full mt-4 text-left border-t border-[var(--ui-border)] pt-3">
          <h4 class="text-xs uppercase font-bold text-[var(--ui-text-muted)] mb-2">Examples / Hints:</h4>
          <div
            v-for="(ex, idx) in card.examples"
            :key="idx"
            class="example-item text-sm my-1.5 p-2.5 rounded-xl border border-[var(--ui-border)] bg-[var(--ui-surface-muted)]"
          >
            <p class="font-medium text-[var(--ui-text)]">{{ ex.original }}</p>
            <p v-if="ex.translation" class="text-xs text-[var(--ui-text-muted)] mt-0.5">{{ ex.translation }}</p>
          </div>
        </div>
      </template>
    </div>

    <!-- Empty / Welcome state matching TaskManager surfaces -->
    <div
      v-else
      class="welcome-card w-full max-w-lg rounded-2xl border p-8 flex flex-col items-center text-center shadow-md backdrop-blur-md"
    >
      <div class="text-5xl mb-4 select-none">🦉</div>
      <h3 class="text-2xl font-bold mb-2 text-[var(--ui-text)]">Welcome to Geobu</h3>
      <p class="text-sm text-[var(--ui-text-muted)] mb-6">
        Practice words, listen to native pronunciations, and expand your vocabulary with spaced repetition.
      </p>
      <p class="text-xs text-[var(--ui-text-subtle)]">
        Tap the action buttons below to start learning.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { UiButton, UiBadge } from '../ui';
import type { CardData } from '../types';
import { mediaCache } from '../services/mediaCacheService';
import { api } from '../services/api';
import { learningEngine, checkTranslationMatch } from '../services/LearningWorkflowEngine';
import { localSync } from '../services/LocalSyncService';

const props = defineProps<{
  card: CardData | null;
}>();

const emit = defineEmits<{
  (e: 'submit-reply', text: string): void;
  (e: 'word-action', action: string, card: CardData): void;
}>();

const isPlayingAudio = ref(false);
const isRevealed = ref(false);

// Writing drill state
const userTranslationInput = ref('');
const isTypingSubmitted = ref(false);
const isTypingCorrect = ref(false);

watch(
  () => [props.card?.word, props.card?.translation, props.card?.level, props.card?.isTranslationResult],
  ([newWord, newTr, newLvl, newRes], [oldWord, oldTr, oldLvl, oldRes] = []) => {
    if (newWord !== oldWord || newTr !== oldTr || newLvl !== oldLvl || newRes !== oldRes) {
      isRevealed.value = false;
      userTranslationInput.value = '';
      isTypingSubmitted.value = false;
      isTypingCorrect.value = false;
    }
  }
);

const submitTyping = () => {
  const input = userTranslationInput.value.trim();
  if (!input) return;

  const expectedWord = (props.card?.word || '').trim();
  const expectedTranslation = (props.card?.translation || '').trim();

  // Match using client-side JS normalization & variant matching
  isTypingCorrect.value =
    checkTranslationMatch(input, expectedWord) ||
    checkTranslationMatch(input, expectedTranslation) ||
    Boolean(expectedWord && input.toLowerCase().includes(expectedWord.toLowerCase())) ||
    Boolean(props.card?.rawText && props.card.rawText.toLowerCase().includes(input.toLowerCase()));

  isTypingSubmitted.value = true;
  emit('submit-reply', input);
};

const handleReaction = async (action: string) => {
  if (!props.card) return;
  emit('word-action', action, props.card);

  const wordId = props.card.wordId || 1;
  await learningEngine.reactToWord(wordId, action, props.card);

  // If online, also notify legacy endpoint for mock compatibility
  if (props.card.wordId && localSync.state.connection !== 'offline' && navigator.onLine) {
    void api
      .reactToWord({
        word_id: props.card.wordId,
        action,
        dictionary_id: props.card.dictionaryId,
      })
      .catch(() => null);
  }

  // Update local card level for instant feedback
  if (action === 'star_up') {
    props.card.level = Math.min((props.card.level || 1) + 1, 5);
  } else if (action === 'star_down') {
    props.card.level = Math.max((props.card.level || 1) - 1, 1);
  }
};


const playPronunciation = async () => {
  if (!props.card?.word) return;
  isPlayingAudio.value = true;

  try {
    if (props.card.audioUrl) {
      const cachedUrl = await mediaCache.getCachedAudioUrl(props.card.audioUrl);
      const audio = new Audio(cachedUrl);
      audio.onended = () => { isPlayingAudio.value = false; };
      audio.onerror = () => { fallbackTts(); };
      await audio.play();
      return;
    }

    fallbackTts();
  } catch {
    fallbackTts();
  }
};

const fallbackTts = () => {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(props.card?.word || '');
    utterance.lang = 'ka-GE';
    utterance.rate = 0.9;
    utterance.onend = () => { isPlayingAudio.value = false; };
    utterance.onerror = () => { isPlayingAudio.value = false; };
    window.speechSynthesis.speak(utterance);
  } else {
    isPlayingAudio.value = false;
  }
};
</script>

<style scoped>
.flashcard-card,
.welcome-card {
  background: var(--ui-card-surface);
  border-color: var(--ui-card-border);
  box-shadow: var(--ui-shadow-elevated);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--ui-text);
}
.audio-button {
  background: var(--ui-surface-hover);
  border: 1px solid var(--ui-border);
}
.word-text {
  color: var(--ui-text);
}
</style>
